import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { useAuth } from './AuthContext';
import { validatePassword } from '../../utils/passwordPolicy';
import { FormPage, PasswordInput } from '../../common/components/FormKit';

const ChangePassword: React.FC = () => {
  const { user, markPasswordChanged } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Пароль выдан системой (восстановление через почту) — показываем приглашение его заменить
  const forced = Boolean(user?.mustChangePassword) || Boolean((location.state as any)?.forced);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Новый пароль и подтверждение не совпадают.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Новый пароль должен отличаться от текущего.');
      return;
    }
    const policyError = validatePassword(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${ApiUrl}/api/Auth/change-password`, { currentPassword, newPassword });
      markPasswordChanged();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Пароль успешно изменён.');
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.message
        ? err.response.data.message
        : 'Не удалось изменить пароль. Попробуйте позже.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const passwordMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;

  return (
    <FormPage title="Смена пароля" variant="narrow">
      <form className="form-page__form" onSubmit={handleSubmit}>
        {forced && !success && (
          <div className="form-page__notice" role="status">
            Вы вошли с временным паролем, который пришёл вам на почту. Рекомендуем сразу заменить его на свой.
            В поле «Текущий пароль» укажите пароль из письма.
          </div>
        )}
        {error && <div className="form-page__alert" role="alert">{error}</div>}
        {success && <div className="form-page__notice" role="status">{success}</div>}

        <div className="form-page__row form-page__row--single">
          <PasswordInput
            id="cp-current"
            label="Текущий пароль"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            disabled={loading}
          />
        </div>

        <div className="form-page__row form-page__row--single">
          <PasswordInput
            id="cp-new"
            label="Новый пароль"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            disabled={loading}
            showRequirements
          />
        </div>

        <div className="form-page__row form-page__row--single">
          <PasswordInput
            id="cp-confirm"
            label="Подтвердите новый пароль"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            disabled={loading}
            invalid={passwordMismatch}
            error={passwordMismatch ? 'Пароли не совпадают' : undefined}
          />
        </div>

        <div className="form-page__actions">
          {forced && !success ? (
            <button type="button" className="btn btn--back" onClick={() => navigate('/')}>
              Сменить позже
            </button>
          ) : (
            <Link to="/" className="form-page__login-link"><span>{success ? 'На главную' : 'Отмена'}</span></Link>
          )}
          <button type="submit" className="btn form-page__submit" disabled={loading}>
            {loading ? 'Сохранение…' : 'Сменить пароль'}
          </button>
        </div>
      </form>
    </FormPage>
  );
};

export default ChangePassword;
